<template>
  <div class="home">

  <Navbar/>

    <Hero
        :img="heroImg"
        intro="Ideas are easy."
        title="Implementation is hard."
        below1="Entrust your internet infrastructure projects to our team. "
        below2="We get it right the first time, every time."
    />


    <div class="container mx-auto m-0 pb-24 pt-6 px-3 sm:px-12 font-sans">

        <div class="text-gray-700 text-base md:text-lg">
            <p class="mb-4">
                At Island Bridge Networks, our engineers are passionate about building robust,
                functional, secure and fault tolerant networks. We design, build and
                operate these networks with 24/7 monitoring and support for our customers.

            </p>
            <p class="mb-4">
                As a company, we take particular pride in being vendor and supplier neutral. This
                allows us to advise our customers in their own best interests and choose the
                most appropriate equipment and suppliers for a given project.
            </p>
        </div>

        <div class="content">
            <h3>ISP Core &amp; Edge Network Management Service</h3>

            <p>
                Our flagship service offering is our <b>complete managed core and edge ISP
                network engineering service</b>. In summary, we design, build and operate an ISPs
                core and edge network using our in-house level-3 network engineers and system administrators.
            </p>
            <p>
                This includes vendor management for hardware, upstream IP and IXP links, and data centre / co-location providers;
                design, build and management of the core and edge networks; all related critical infrastructure
                including authoritative DNS, recursive DNS, NTP, route reflectors, DDoS mitigation, AAA/RADIUS;
                and management infrastructure including support channels to us, monitoring and alerting, graphing
                and dashboards, documentation, VPN access and IPAM. If required, we can also build the infrastructure
                for VoIP telephony services.
            </p>
            <p>
                This is a unique offering which allows the ISP business leaders to concentrate on: sales and support;
                customer retention; expanding services and products; building and maintaining the access network; and
                financing and accounts. All with the reassurance that their core network is being professionally managed
                and monitored.
            </p>


            <h3>Services We Offer</h3>

            <p>
                Many of the skills we use to provide our <em>ISP Core &amp; Edge Network Management Service</em> are
                individually available as professional services.
            </p>

        </div>

        <div class="flex flex-row flex-wrap items-stretch -mx-2">

            <Card v-for="(card, index) in cards" :key="index" v-bind:card="card" />

        </div>

    </div>


    <Footer/>

  </div>
</template>

<script>
import Card     from '../components/Card.vue'
import Hero     from '../components/Hero.vue'
import Footer   from '../components/Footer.vue'
import Navbar   from '../components/Navbar.vue'

export default {
  name: 'services',
  beforeCreate: function() {
      document.body.className = 'bg-gray-200';
  },
  components: {
    Footer, Navbar,
    Card,
    Hero,
  },
  data () {
    return {
      heroImg: require('@/assets/bg-datacentre.jpg'),

      cards: [
          {
              title: 'Network Design',
              content: 'Through a deep analysis of your infrastructure needs, our consultants will work to develop an appropriate network design that is scalable and future-proof.',
              tags: [ 'IP', 'BGP', 'OSPF', 'ISIS', 'FHRP' ]
          },
          {
              title: 'Vendor Management & Procurement',
              content: 'As a vendor neutral company with extensive market knowledge, we can design and run procurement processes to find the most appropriate products at the best price.',
              tags: [ 'RFP', 'RFT', 'Procurement' ]
          },
          {
              title: 'Network Security',
              content: 'Full-stack network security consultancy from physical infrastructure through network device and service configuration to DDoS prevention and mitigation.',
              tags: [ 'Firewall', 'DDoS', 'CVE', 'RPKI' ]
          },
          {
              title: 'IPv6 & Transition Mechanisms',
              content: 'With over 20 years of IPv6 experience, we can design and build dual-stacked IPv6 networks and help with transition mechanisms including CG-NAT and core services.',
              tags: [ 'IPv6', 'Dual-Stack', 'NAT64', 'CG-NAT' ],
          },
          {
              title: 'VoIP and Softswitches',
              content: 'We build and manage VoIP softswitches to enable our customers to provide telephony services to their own customers (including billing systems).',
              tags: [ 'SIP', 'openSIPs', 'Kamailio', 'Asterisk' ]
          },
          {
              title: 'Network Management',
              content: 'We provide a 24/7/365 NOC service with a ticket-managed helpdesk; on-call engineers; proactive monitoring; incident management, escalation and RFO assistance.',
              tags: [ '24/7/365', 'NOC', 'Helpdesk', 'RFO' ],
          },
          {
              title: 'Fibre Infrastructure Services',
              content: 'Productisation, design and management of fibre assets for service delivery to both the retail and wholesale markets.',
              tags: [ 'DWDM', 'Passive / Active', '100G', '400G' ]
          },
          {
              title: 'Network Automation',
              content: 'Improve service availability and management with network automation from provisioning through to billing, monitoring and support.',
              tags: [ 'Napalm', 'SaltStack', 'Ansible' ]
          },
          {
              title: 'Data Centre Services',
              content: 'We provide data centre technical services which include full supply, design, installation, configuration and support of data centre deployments and migrations.',
              tags: [ 'Remote Hands', 'KVM', 'Smart PDUs' ]
          },
          {
              title: 'Network Productisation',
              content: 'Leverage your network to its full revenue potential - we will help you identify new products and services as well as adding efficiencies to current processes.',
              tags: [ 'Revenue', 'Asset Utilisation', 'Product Design' ]
          },
          {
              title: 'Project Audit & Verification',
              content: 'We can provide network audit services to ensure projects are delivered to specification or provide a second opinion via our design verification service.',
              tags: [ 'Audit', 'Verification', 'Validation' ]
          },
          {
              title: 'Peering Strategy & Negotiation',
              content: 'Good peering is built on good relationships. Build on our experience with hundreds of carriers worldwide to obtain peering agreements that deliver the results you need.',
              tags: [ 'Relationships', 'Negotiation', 'Peering' ]
          },
          {
              title: 'RIPE (and other RIR) Consulting',
              content: 'We offer RIR consulting including: LIR set-up, management and merging services; RIPE/RIR database record management; routing and RPKI records; and resource transfer.',
              tags: [ 'RIPE', 'APNIC', 'ARIN', 'AFRINIC', 'LACNIC' ]
          },
          {
              title: 'M&A Audit and Review',
              content: 'If you are buying or selling a business, our consultants perform: network infrastructure audits; inventory and asset verifications; and risk, gap and opportunity analysis.',
              tags: [ 'M&A', 'SWOT', 'Audit', 'Verification' ]
          },
          {
              title: 'Training & Workshops',
              content: 'We provide training and hands-on workshops around our core competencies: network engineering for front-line support and up, VoIP and PHP development with Laravel.',
              tags: [ 'Training', 'Workshops' ]
          },

      ],

    }
  }
}
</script>
